import {isNotNil} from 'ramda';

import {useLazyCalculateFromWithoutVatQuery} from '@dms/api/core';
import {
  OneTimePrice,
  RecurringPriceCalculated,
  RecurringPriceSimple,
  useAddVehicleCostMutation,
  useGetActiveSaleVehicleQuery,
  useListVehicleCostCategoriesQuery,
} from '@dms/api/vehicle';

import {DeepNonNullableWithPartial, RequiredTestIdProps} from 'shared/web';

import {useSaleVehicleActions} from '../../hooks/useSaleVehicleActions';
import {useTenant} from '../../hooks/useTenant';
import {handleApiError} from '../../utils/handleApiError';
import {useSubscribeToFormChanges} from '../VehicleTransactionForm/hooks/useSubscribeToFormChanges';
import {
  VehicleSimplifiedTransactionForm,
  VehicleSimplifiedTransactionFormRef,
} from '../VehicleTransactionForm/VehicleSimplifiedTransactionForm';
import {
  VehicleTransactionForm,
  VehicleTransactionFormRef,
} from '../VehicleTransactionForm/VehicleTransactionForm';
import {VatCodePrice} from '../VehicleTransactionForm/VehicleTransactionFormSchema';

type FullFormSubmitHandler = Parameters<typeof VehicleTransactionForm>[0]['onSubmit'];
type SimplifiedFormSubmitHandler = Parameters<
  typeof VehicleSimplifiedTransactionForm
>[0]['onSubmit'];

interface VehicleAddCostDialogProps extends RequiredTestIdProps {
  vehicleId: string;
  onSuccess?: (id: string) => void;
  onDiscard?: VoidFunction;
  openSimplified?: boolean;
}

export function VehicleAddCostForm(props: VehicleAddCostDialogProps) {
  const {tenantCountry, tenantCurrency} = useTenant();

  const {data: activeSaleVehicle} = useGetActiveSaleVehicleQuery({vehicleId: props.vehicleId});

  const [addVehicleCost] = useAddVehicleCostMutation();
  const {isSaleActionEnabled} = useSaleVehicleActions(props.vehicleId);

  const [calculatePriceWithoutVat] = useLazyCalculateFromWithoutVatQuery();

  const formRef = useSubscribeToFormChanges<VehicleTransactionFormRef>({
    watchFields: ['category'],
    onChange: (value, {formApi, formValues}) => {
      if (value.category?.[0] === 'FINANCE_AND_INSURANCE') {
        formApi?.setValue('expectedPrice.vatType', 'Z', {
          shouldDirty: true,
        });
        formApi?.setValue('realPrice.vatType', 'Z', {
          shouldDirty: true,
        });
        const expectedPrice = formValues.expectedPrice?.withoutVat?.amount;
        const realPrice = formValues.realPrice?.withoutVat?.amount;
        if (isNotNil(expectedPrice)) {
          calculatePriceWithoutVat({
            vatRateType: 'Z',
            amount: expectedPrice,
            countryCode: tenantCountry,
          })
            .unwrap()
            .then((response) => {
              formApi?.setValue('expectedPrice.withVat.amount', response.withVat, {
                shouldDirty: true,
              });
            })
            .catch(handleApiError);
        }
        if (isNotNil(realPrice)) {
          calculatePriceWithoutVat({
            vatRateType: 'Z',
            amount: realPrice,
            countryCode: tenantCountry,
          })
            .unwrap()
            .then((response) => {
              formApi?.setValue('realPrice.withVat.amount', response.withVat, {
                shouldDirty: true,
              });
            })
            .catch(handleApiError);
        }
      }
    },
  });

  const formRefSimplified = useSubscribeToFormChanges<VehicleSimplifiedTransactionFormRef>({
    watchFields: ['category'],
    onChange: (value, {formApi, formValues}) => {
      if (value.category?.[0] === 'FINANCE_AND_INSURANCE') {
        formApi?.setValue('expectedPrice.vatType', 'Z', {
          shouldDirty: true,
        });
        const expectedPrice = formValues.expectedPrice?.withoutVat?.amount;
        if (isNotNil(expectedPrice)) {
          calculatePriceWithoutVat({
            vatRateType: 'Z',
            amount: expectedPrice,
            countryCode: tenantCountry,
          })
            .unwrap()
            .then((response) => {
              formApi?.setValue('expectedPrice.withVat.amount', response.withVat, {
                shouldDirty: true,
              });
            })
            .catch(handleApiError);
        }
      }
    },
  });

  const {data: categories} = useListVehicleCostCategoriesQuery({});

  const onSubmitFullForm: FullFormSubmitHandler = async (data) => {
    const {realPrice, expectedPrice, transactionType, calculationType, interestPA, ...rest} =
      data as DeepNonNullableWithPartial<typeof data>;

    const isOneTime = transactionType?.[0] === 'ONE_TIME' && realPrice;
    const isRecurring = transactionType?.[0] === 'RECURRING' && realPrice;
    const isSimpleCalculation = isRecurring && calculationType?.[0] === 'SIMPLE' && realPrice;
    const isCalculatedCalculation =
      isRecurring && calculationType?.[0] === 'CALCULATED' && realPrice;

    // Transform prices using helper functions
    const transformedExpectedPrice = transformToOneTimePrice(expectedPrice);
    const transformedRealPriceOneTime =
      !isRecurring && isOneTime ? transformToOneTimePrice(realPrice) : null;

    await addVehicleCost({
      vehicleId: props.vehicleId,
      addVehicleCostRequestBody: {
        ...rest,
        category: (rest.category?.[0] ?? null) as string | null,
        note: rest.note ?? null,
        expectedPrice: transformedExpectedPrice,
        realPriceOneTime: transformedRealPriceOneTime,
        realPriceRecurringCalculated: isCalculatedCalculation
          ? transformCalculatedRecurringPrice(
              realPrice,
              rest.startDate,
              rest.endDate,
              interestPA,
              tenantCurrency
            )
          : null,
        realPriceRecurringSimple: isSimpleCalculation
          ? transformSimpleRecurringPrice(
              data.realPrice!,
              rest.startDate,
              rest.endDate,
              tenantCurrency
            )
          : null,
      },
    })
      .unwrap()
      .then((response) => {
        props.onSuccess?.(response.id);
      })
      .catch(handleApiError);
  };

  const onSubmitSimplifiedForm: SimplifiedFormSubmitHandler = async (data) => {
    const transformedExpectedPrice = transformToOneTimePrice(data.expectedPrice);
    await addVehicleCost({
      vehicleId: props.vehicleId,
      addVehicleCostRequestBody: {
        name: data.name,
        category: (data.category?.[0] ?? null) as string | null,
        expectedPrice: transformedExpectedPrice,
        realPriceOneTime: null,
        realPriceRecurringCalculated: null,
        realPriceRecurringSimple: null,
        note: null,
        fileId: null,
      },
    })
      .unwrap()
      .then((response) => {
        props.onSuccess?.(response.id);
      })
      .catch(handleApiError);
  };

  return props.openSimplified ? (
    <VehicleSimplifiedTransactionForm
      data-testid={props['data-testid']}
      showCategories
      categories={categories ?? []}
      onSubmit={onSubmitSimplifiedForm}
      onDiscard={props.onDiscard}
      currency={tenantCurrency}
      countryCode={tenantCountry}
      canSetExpectedPrice={isSaleActionEnabled('SALE_VEHICLE_ADD_EXPECTED_COST')}
      saleVehicle={activeSaleVehicle}
      ref={formRefSimplified}
    />
  ) : (
    <VehicleTransactionForm
      data-testid={props['data-testid']}
      canSetExpectedPrice={isSaleActionEnabled('SALE_VEHICLE_ADD_EXPECTED_COST')}
      onSubmit={onSubmitFullForm}
      onDiscard={props.onDiscard}
      currency={tenantCurrency}
      countryCode={tenantCountry}
      showCategories
      categories={categories ?? []}
      soldAt={activeSaleVehicle?.soldAt}
      ref={formRef}
    />
  );
}

function transformToOneTimePrice(price: VatCodePrice | null | undefined): OneTimePrice | null {
  if (!price || price.withoutVat?.amount === null) {
    return null;
  }

  if (!price.vatDirection || !price.vatRate || !price.vatType) {
    return null;
  }

  return {
    vatType: price.vatType,
    vatRate: price.vatRate,
    withoutVat: price.withoutVat,
    withVat: price.withVat,
    vatDirection: price.vatDirection,
  } as OneTimePrice;
}

function transformSimpleRecurringPrice(
  vatCodePrice: VatCodePrice,
  startDate: string,
  endDate: string,
  currency: string
): RecurringPriceSimple {
  return {
    startDate,
    endDate,
    price: {
      amount: vatCodePrice.withoutVat.amount ?? '0',
      currency: vatCodePrice.withVat.currency ?? currency,
    },
  };
}

function transformCalculatedRecurringPrice(
  vatCodePrice: VatCodePrice,
  startDate: string,
  endDate: string,
  interestPA: number,
  currency: string
): RecurringPriceCalculated {
  return {
    startDate,
    endDate,
    basePrice: {
      amount: vatCodePrice.withoutVat.amount ?? '0',
      currency: vatCodePrice.withoutVat.currency ?? currency,
    },
    interestRate: String(interestPA),
  };
}
